import { Checkbox, Input } from "antd";
import { Search } from "lucide-react";

import type { ShopRoleBriefVO } from "@/api/employees/type";

import { getRoleKey } from "./transferUtils";

export interface RoleTransferPanelProps {
  /** 面板标题 */
  title: string;
  /** 未过滤时的总数 */
  count: number;
  /** 当前展示的角色（已按本侧关键词过滤） */
  items: ShopRoleBriefVO[];
  /** 已勾选的角色名称 */
  selectedKeys: string[];
  /** 本侧搜索关键词 */
  keyword: string;
  /** 搜索占位文案 */
  searchPlaceholder: string;
  /** 无数据且无搜索时的空态文案 */
  emptyText: string;
  /** 修改本侧搜索关键词 */
  onKeywordChange: (value: string) => void;
  /** 勾选 / 取消单个角色 */
  onToggle: (roleName: string, checked: boolean) => void;
  /** 全选 / 取消全选当前可见列表 */
  onToggleAll: (checked: boolean, items: ShopRoleBriefVO[]) => void;
}

/**
 * 角色穿梭单侧面板：本侧搜索 + 全选 + 角色列表
 */
export function RoleTransferPanel({
  title,
  count,
  items,
  selectedKeys,
  keyword,
  searchPlaceholder,
  emptyText,
  onKeywordChange,
  onToggle,
  onToggleAll,
}: RoleTransferPanelProps) {
  const itemKeys = items
    .map((role) => getRoleKey(role))
    .filter((key): key is string => Boolean(key));
  // 当前可见列表是否全部勾选 / 部分勾选（半选）
  const allSelected = itemKeys.length > 0 && itemKeys.every((key) => selectedKeys.includes(key));
  const someSelected = itemKeys.some((key) => selectedKeys.includes(key));

  return (
    <div className="flex flex-1 flex-col min-w-0 min-h-0 border border-slate-200 rounded-lg bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-2 shrink-0 px-3 py-2.5 bg-slate-50 border-b border-slate-200">
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-xs bg-orange-50 text-orange-600">
          {count}
        </span>
      </div>
      <div className="shrink-0 px-3 pt-2.5">
        <Input
          allowClear
          value={keyword}
          onChange={(event) => onKeywordChange(event.target.value)}
          placeholder={searchPlaceholder}
          prefix={<Search className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.5} />}
        />
      </div>
      <div className="flex-1 min-h-0 mt-2 px-2 pb-2 overflow-y-auto">
        {items.length ? (
          <>
            <label className="flex items-center gap-2 px-2 py-2 mb-1 rounded-md cursor-pointer text-xs text-slate-500 hover:bg-orange-50/60">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={(event) => onToggleAll(event.target.checked, items)}
              />
              <span>全部勾选</span>
            </label>
            {items.map((role) => {
              const key = getRoleKey(role);
              if (!key) return null;
              const checked = selectedKeys.includes(key);
              const systemNames = role.systemTypeNames?.filter(Boolean).join("、") || "—";
              const permissionCount = role.permissionCount ?? 0;
              return (
                <label
                  key={key}
                  className="flex items-start gap-2 px-2 py-2 rounded-md cursor-pointer hover:bg-orange-50/60"
                >
                  <Checkbox
                    className="mt-0.5"
                    checked={checked}
                    onChange={(event) => onToggle(key, event.target.checked)}
                  />
                  <span className="flex flex-1 flex-col min-w-0 gap-0.5">
                    <span className="block truncate text-sm text-slate-800">{key}</span>
                    <span className="block truncate text-xs text-slate-400">
                      {systemNames} · {permissionCount} 项权限
                    </span>
                  </span>
                </label>
              );
            })}
          </>
        ) : (
          <div className="flex items-center justify-center min-h-[120px] px-4 text-sm text-slate-400">
            {keyword.trim() ? "未找到匹配角色" : emptyText}
          </div>
        )}
      </div>
    </div>
  );
}
